import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Box, Typography, MenuItem, useTheme, Tooltip, CircularProgress, MenuList } from '@material-ui/core';
import { FormProvider } from 'react-hook-form';
import noop from 'lodash/noop';
import { PiSparkleBold } from 'react-icons/pi';
import { BiAbacus } from 'react-icons/bi';

import {
  AppChip,
  AppIconButton,
  ConfirmDialog,
  GridActions,
  GridCellWithAvatar,
} from '@vyce/core/src/components';
import { GRID_PAGE_SIZE } from '@vyce/core/src/constants';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { useBooleanState } from '@vyce/core/src/hooks';
import { getAvatar } from '@vyce/core/src/utils/getAvatar';
import { WidgetWrapper, WidgetWrapperProps } from '@vyce/core/src/components/Dashboard/WidgetWrapper';
import { TextStatusComponent } from '@vyce/core/src/components/Statuses/TextStatusComponent';
import { EnrichedColumnProps } from '@vyce/core/src/components/InfiniteTable/types';
import { InfiniteTable } from '@vyce/core/src/components/InfiniteTable';
import { TeamQualificationWidgetDTO } from '@vyce/core/src/api/types';
import { AIAnalyticsDialog } from '@vyce/core/src/components/AIAnalitics/AIAnalyticsDialog';
import { deleteTeamMembersFromModuleRequest } from '@vyce/core/src/api/teams';
import useChatRequests from '@vyce/core/src/hooks/useChatRequests';

import { useWidgetData } from './hooks';
import { Props } from './types';
import { MATCH_MODES, MatchMode, defaultStatusOptions, matchModeSummaryLabel, statuses } from './config';
import { FilterDialog } from './components';
import { RiDownloadCloud2Line } from 'react-icons/ri';
import { useFormAIAnalytics } from './hooks/useAIAnaytics';
import { HiOutlineTrash } from 'react-icons/hi';

/* Team's Qualifications widget */
export const QualificationWidget = (props: Props & WidgetWrapperProps) => {
  const theme = useTheme();
  const { showTeamsSelector = true, selectedCompanyId, chatUser, chatCompanyId, chatRoute } = props;
  const { handleServerError, showNotification } = useContext(NotificationContext);
  const { createPrivateChat } = useChatRequests();
  const [closeGridAction, setCloseGridAction] = useState(false);
  const [isConfirmDialogOpen, openConfirmDialog, closeConfirmDialog] = useBooleanState(false);
  const [isRemoving, setRemovingTrue, setRemovingFalse] = useBooleanState(false);
  const [userToRemove, setUserToRemove] = useState<TeamQualificationWidgetDTO>();
  const [fullscreenContainer, setFullscreenContainer] = useState<HTMLElement | null>(null);

  const {
    teamQualifications,
    qualificationsSelectOptions,
    jobTitleOptions,
    filterUsersSelectOptions,
    selectedTeamOptions,
    unselectedTeamOptions,
    total,
    selectedStatusIds,
    matchMode,
    setMatchMode,
    methods,
    isLoadingList,
    loading,
    resetLoadingList,
    setSelectedStatusIds,
    getTeamQualifications,
    exportQualifications,
    exportLoading,
    getDataForAIAnalytics,
    isOpenFilterDialog,
    setFilterDialogOpen,
    setFilterDialogFalse,
    selectedTeamName,
  } = useWidgetData(props);

  const menuProps = useMemo(() => {
    return fullscreenContainer ? { container: fullscreenContainer } : {};
  }, [fullscreenContainer]);

  useEffect(() => {
    const getFullscreenElement = () => {
      return (
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement ||
        null
      );
    };

    const updateContainer = () => {
      const fullscreenEl = getFullscreenElement();
      setFullscreenContainer(fullscreenEl as HTMLElement | null);
    };

    updateContainer();

    const events = [
      'fullscreenchange',
      'webkitfullscreenchange',
      'mozfullscreenchange',
      'MSFullscreenChange',
    ];
    events.forEach(event => {
      document.addEventListener(event, updateContainer);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateContainer);
      });
    };
  }, []);

  const resetCloseStatus = useCallback(() => {
    setCloseGridAction(true);
    setTimeout(() => setCloseGridAction(false), 100);
  }, []);

  const handleRemoveClick = useCallback(
    (user: TeamQualificationWidgetDTO) => {
      resetCloseStatus();
      setUserToRemove(user);
      openConfirmDialog();
    },
    [openConfirmDialog, resetCloseStatus]
  );

  const goToChat = async (user: TeamQualificationWidgetDTO) => {
    const companyId = chatCompanyId ?? selectedCompanyId;
    if (!chatUser || !companyId) {
      return;
    }
    await createPrivateChat({
      creator: chatUser,
      chatUser: {
        uid: user.user_id,
        name: `${user.first_name} ${user.last_name}`,
        email: user.email,
        phone: user.phone,
        avatarUrl: user.photo,
      },
      companyId,
      messagesRoute: chatRoute,
    });
  };

  const removeFromModule = useCallback(async () => {
    if (!userToRemove?.user_id || !selectedCompanyId) {
      return;
    }

    try {
      setRemovingTrue();
      const res = await deleteTeamMembersFromModuleRequest(selectedCompanyId, {
        members: [userToRemove.user_id],
      });
      showNotification?.({ message: res.data.message, options: { variant: 'success' } });
      closeConfirmDialog();
      setUserToRemove(undefined);

      resetLoadingList();
      await getTeamQualifications(0);
    } catch (e) {
      handleServerError(e);
    } finally {
      setRemovingFalse();
    }
  }, [
    closeConfirmDialog,
    getTeamQualifications,
    handleServerError,
    resetLoadingList,
    selectedCompanyId,
    setRemovingFalse,
    setRemovingTrue,
    showNotification,
    userToRemove?.user_id,
  ]);

  const {
    aiAnalytics,
    progress,
    setProgress,
    dataLoading,
    regenerateAIAnalytics,
    focusOfAnalysis,
    setFocusOfAnalysis,
    openAIAnalyticsDialog,
    setOpenAIAnalyticsDialog,
    handleClose,
  } = useFormAIAnalytics({
    getDataForAIAnalytics,
  });

  const { watch } = methods;
  const qualifications = watch('qualifications');
  const filterUsers = watch('filterUsers');
  const jobTitles = watch('jobTitles');

  const gridActionsCloseSignal = closeGridAction;

  const statusSummaryText = useMemo(() => {
    if (!selectedStatusIds?.length) {
      return 'any status';
    }
    return selectedStatusIds
      .map(id => defaultStatusOptions.find(o => o.value === id)?.name)
      .filter(Boolean)
      .join(', ');
  }, [selectedStatusIds]);

  const qualificationsSummaryText = useMemo(() => {
    if (!qualifications?.length) {
      return 'any qualification';
    }
    return `${matchModeSummaryLabel[matchMode]} ${qualifications.join(', ')}`;
  }, [qualifications, matchMode]);

  const membersSummaryText = useMemo(() => {
    if (!filterUsers?.length) {
      return 'any member';
    }
    if (filterUsers.length === 1) {
      return filterUsers[0].name;
    }
    return `${filterUsers.length} members`;
  }, [filterUsers]);

  const jobTitlesSummaryText = useMemo(() => {
    if (!jobTitles?.length) {
      return 'any job title';
    }
    return jobTitles.join(', ');
  }, [jobTitles]);

  const columns: EnrichedColumnProps<TeamQualificationWidgetDTO>[] = useMemo(
    () => [
      {
        field: 'first_name',
        headerName: 'Name',
        flex: 1,
        width: 150,
        sortable: false,
        renderCell: row => (
          <GridCellWithAvatar
            createPrivateChat={chatUser ? () => goToChat(row) : undefined}
            avatarUrl={row.photo}
            avatarPlaceholder={getAvatar(row.gender)}
            name={`${row.first_name} ${row.last_name}`}
            link={`/teams/members/${row?.first_name}_${row?.user_id}`}
            target="_blank"
          />
        ),
      },
      {
        field: 'title',
        headerName: 'Qualification',
        flex: 0.8,
        width: 100,
        sortable: false,
        valueGetter: row => row?.title ?? '',
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 0.8,
        width: 100,
        sortable: false,
        renderCell: row => (
          <TextStatusComponent
            verified={row?.status !== statuses.Indefinite}
            expiry_date={row?.expiry_date}
            status={row?.status}
          />
        ),
      },
      {
        field: 'customCell',
        headerName: 'Actions',
        width: 80,
        hideSortIcons: true,
        sortable: false,
        disableColumnMenu: true,
        renderCell: row => (
          <Box display="flex" width="100%">
            <GridActions close={gridActionsCloseSignal}>
              <MenuList>
                <MenuItem
                  cy-test-id="remove-from-team-button"
                  onClick={() => handleRemoveClick(row as TeamQualificationWidgetDTO)}>
                  <Box display="flex" alignItems="center" gridGap={8}>
                    <HiOutlineTrash size={14} /> Remove from Teams module
                  </Box>
                </MenuItem>
              </MenuList>
            </GridActions>
          </Box>
        ),
      },
    ],
    [gridActionsCloseSignal, handleRemoveClick]
  );

  const handleStatusChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setSelectedStatusIds(event.target.value as string[]);
  };

  const handleMatchModeChange = (mode: MatchMode) => {
    setMatchMode(mode);
    // Status is not applicable when matching workers who hold none of the selected qualifications.
    if (mode === MATCH_MODES.hasNone) {
      setSelectedStatusIds([]);
    }
  };

  return (
    <WidgetWrapper
      {...props}
      renderExtraControls={() => (
        <Box display="flex" alignItems="center" gridGap={8}>
          <Tooltip title="Filter">
            <AppIconButton variant="paper" isSmall isBorder onClick={setFilterDialogOpen}>
              <BiAbacus size="20px" color={theme.palette.text.primary} />
            </AppIconButton>
          </Tooltip>

          <Tooltip title="AI Analytics">
            <AppIconButton variant="paper" isSmall isBorder onClick={() => setOpenAIAnalyticsDialog(true)}>
              <PiSparkleBold size="20px" color={theme.palette.text.primary} />
            </AppIconButton>
          </Tooltip>

          <Tooltip title="Export Qualifications">
            <AppIconButton
              variant="paper"
              isSmall
              isBorder
              onClick={exportQualifications}
              disabled={exportLoading}>
              {exportLoading ? (
                <CircularProgress size={16} />
              ) : (
                <RiDownloadCloud2Line size="20px" color={theme.palette.text.primary} />
              )}
            </AppIconButton>
          </Tooltip>
        </Box>
      )}>
      <Box width="100%" height="100%" display="flex" flexDirection="column" gridGap={12}>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(noop)}>
            <Box width="100%">
              <Typography variant="body1" color="textPrimary" component="span" display="block">
                <Box component="strong">{total}</Box> {total === 1 ? 'Qualification' : 'Qualifications'}
                {showTeamsSelector && (
                  <>
                    {' for '}
                    <Box component="strong" onClick={setFilterDialogOpen} style={{ cursor: 'pointer' }}>
                      {selectedTeamName || '—'}
                    </Box>
                  </>
                )}
                {' · '}
                <Box component="strong" onClick={setFilterDialogOpen} style={{ cursor: 'pointer' }}>
                  {membersSummaryText}
                </Box>
                {' · '}
                <Box component="strong" onClick={setFilterDialogOpen} style={{ cursor: 'pointer' }}>
                  {jobTitlesSummaryText}
                </Box>
                {' · '}
                <Box component="strong" onClick={setFilterDialogOpen} style={{ cursor: 'pointer' }}>
                  {qualificationsSummaryText}
                </Box>
                {matchMode !== MATCH_MODES.hasNone && (
                  <>
                    {' · '}
                    <Box component="strong" onClick={setFilterDialogOpen} style={{ cursor: 'pointer' }}>
                      {statusSummaryText}
                    </Box>
                  </>
                )}
              </Typography>
            </Box>
          </form>
        </FormProvider>

        <Box flex={1} minHeight={0}>
          <InfiniteTable
            isLoading={loading}
            data={teamQualifications}
            columns={columns}
            rowCount={total}
            rowHeight={54}
            height="100%"
            pageSize={GRID_PAGE_SIZE}
            isLoadingList={isLoadingList}
            onLoadMore={getTeamQualifications}
            resetLoadingList={resetLoadingList}
          />
        </Box>
      </Box>

      <FilterDialog
        isOpen={isOpenFilterDialog}
        onClose={setFilterDialogFalse}
        onUpdate={noop}
        methods={methods}
        showTeamsSelector={showTeamsSelector}
        groupOneOptions={selectedTeamOptions}
        groupTwoOptions={unselectedTeamOptions}
        qualificationsSelectOptions={qualificationsSelectOptions}
        jobTitleOptions={jobTitleOptions}
        filterUsersSelectOptions={filterUsersSelectOptions}
        selectedStatusIds={selectedStatusIds}
        onStatusChange={handleStatusChange}
        matchMode={matchMode}
        onMatchModeChange={handleMatchModeChange}
        menuProps={menuProps}
        dialogContainer={fullscreenContainer}
      />

      <AIAnalyticsDialog
        open={openAIAnalyticsDialog}
        onClose={handleClose}
        resultText={aiAnalytics}
        progress={progress}
        setProgress={setProgress}
        loading={progress < 100}
        regenerateAIAnalytics={regenerateAIAnalytics}
        loadingText={dataLoading ? 'Getting data...' : 'Analyzing the data...'}
        focusOfAnalysis={focusOfAnalysis}
        onFocusOfAnalysisChange={setFocusOfAnalysis}
      />

      <ConfirmDialog
        handleClose={closeConfirmDialog}
        open={!!isConfirmDialogOpen}
        confirmText="Remove"
        cancelText="Cancel"
        title={`Are you sure you want to remove ${userToRemove?.first_name ?? ''} ${
          userToRemove?.last_name ?? ''
        } from Teams module?`}
        handleConfirm={removeFromModule}
        loading={isRemoving}
      />
    </WidgetWrapper>
  );
};
